// Pure pricing/discount/tax domain math - no React, no side effects, no
// toast coupling. Ported out of src/components/utils/BillingItems/** (the
// old, still-in-use-by-5-other-features pricing engine) and
// src/utils/calculatePrice.js, which is where this logic previously lived
// duplicated across 2-4 files each. This module owns the FORMULA only; the
// entity-agnostic adapter (mapping a plans/products/lineItems collection
// into these functions' input shape) lives in
// _components/billing/hooks/usePricingState.js, not here - both the
// proposal wizard and the subscription modal share that one hook, driving
// this same tested formula, instead of each re-porting the math.

// Billing-cycle -> days conversion. Two slightly different maps existed
// before this file: src/utils/calculatePrice.js used
// {Quarterly:90, "Half-yearly":180}, while BillingItems/index.js used
// {Quarterly:92, "Half-yearly":184}. Canonicalized on the 92/184 values -
// closer to a real calendar average (365/4≈91.25, 365/2=182.5) - since
// that's the pair actually driving today's live proposal totals.
export const CYCLE_TO_DAYS = {
  Daily: 1,
  Weekly: 7,
  Monthly: 30,
  Quarterly: 92,
  "Half-yearly": 184,
  Yearly: 365,
};

// Calendar cycles convert between each other by month ratio, never by day
// count. Day-count conversion between calendar cycles is internally
// inconsistent (Monthly=30 but Yearly=365 made a $40/monthly plan billed
// yearly cost 40/30*365 = $486.67 instead of 40*12 = $480 - D30) and never
// matches what "billed yearly" means to a customer. CYCLE_TO_DAYS remains
// the right bridge only when a sub-monthly cycle (Daily/Weekly) is on
// either side of the conversion.
export const CYCLE_TO_MONTHS = {
  Monthly: 1,
  Quarterly: 3,
  "Half-yearly": 6,
  Yearly: 12,
};

/**
 * Convert `amount` priced per `fromCycle` into its per-`toCycle` price.
 * Month-ratio between calendar cycles (Monthly/Quarterly/Half-yearly/
 * Yearly), day-ratio when Daily or Weekly is involved. Returns the
 * unrounded number; callers round for display. Null cycles or same cycle
 * return the amount unchanged.
 */
export function convertAmountBetweenCycles(amount, fromCycle, toCycle) {
  const fromKey = normalizeCycleLabel(fromCycle);
  const toKey = normalizeCycleLabel(toCycle);
  if (!fromKey || !toKey || fromKey === toKey) return amount;

  const fromMonths = CYCLE_TO_MONTHS[fromKey];
  const toMonths = CYCLE_TO_MONTHS[toKey];
  if (fromMonths && toMonths) {
    return (amount / fromMonths) * toMonths;
  }

  const fromDays = CYCLE_TO_DAYS[fromKey];
  const toDays = CYCLE_TO_DAYS[toKey];
  if (!fromDays || !toDays) return amount;
  return (amount / fromDays) * toDays;
}

/** Align with server utils/pricing normalizeCycleLabel. */
export function normalizeCycleLabel(cycle) {
  if (cycle == null || cycle === "") return null;
  const lower = String(cycle).trim().toLowerCase().replace(/[\s_]+/g, "-");
  switch (lower) {
    case "daily":
    case "day":
      return "Daily";
    case "weekly":
    case "week":
      return "Weekly";
    case "monthly":
    case "month":
      return "Monthly";
    case "quarterly":
    case "quarter":
      return "Quarterly";
    case "half-yearly":
    case "half-year":
    case "semi-annual":
    case "semi-annually":
      return "Half-yearly";
    case "yearly":
    case "year":
    case "annual":
    case "annually":
      return "Yearly";
    default:
      return null;
  }
}

export function getCycleDays(cycle) {
  const key = normalizeCycleLabel(cycle);
  return key ? CYCLE_TO_DAYS[key] ?? 1 : 1;
}

function round2(value) {
  return parseFloat(value.toFixed(2));
}

/**
 * How many wallet-credit "uses" `quantity` seats convert to under the
 * PROPOSAL's selected billing cycle, when the plan/product's own credits
 * are denominated in ITS pricing cycle - e.g. a plan selling 30 credits/
 * Daily, added at qty 1 to a Monthly proposal, grants
 * 1 * (30 days / 1 day) = 30 credits per (monthly) cycle. Ported verbatim
 * from BillingItems/PlanProductTable's calculateCreditsPerBillingCycle.
 */
export function calculateCreditsPerBillingCycle(quantity, pricingCycle, selectedBillingCycle) {
  const cycleMultiplier = getCycleDays(selectedBillingCycle) / getCycleDays(pricingCycle || selectedBillingCycle);
  return Math.round((parseFloat(quantity) || 0) * cycleMultiplier);
}

/**
 * Splits a quantity across a {from,to,unitPrice} bracket array for **Tiered
 * (graduated)** pricing — each bracket's rate applies only to units in that
 * band. Volume (classic) uses `calculateVolumePrice` instead: all units at
 * the single matching bracket rate (matches server `utils/pricing`).
 */
export function computeTierBreakdown(pricingTiers, quantity) {
  let remaining = parseFloat(quantity) || 0;

  const sorted = [...(pricingTiers || [])].sort(
    (a, b) => (parseFloat(a?.from) || 0) - (parseFloat(b?.from) || 0)
  );

  return sorted.map((tier) => {
    const from = parseFloat(tier?.from) || 0;
    const to = tier?.to !== undefined && tier?.to !== "" ? parseFloat(tier.to) : Infinity;
    const unitPrice = parseFloat(tier?.unitPrice) || 0;
    // `from` on the first tier is conventionally 0 as a "no usage yet"
    // sentinel, not a real billable unit - there is no unit #0. Flooring it
    // at 1 before this inclusive-count formula stops that tier from being
    // counted as if it had one extra unit (matches the server-side fix in
    // simplifyserver's utils/pricing/index.js - see demo-bugs.md bug #38).
    const bracketSize =
      to === Infinity ? Math.max(remaining, 0) : Math.max(to - Math.max(from, 1) + 1, 0);
    const quantityInTier = Math.max(Math.min(remaining, bracketSize), 0);
    const tierPrice = round2(quantityInTier * unitPrice);
    remaining -= quantityInTier;
    return { from, to, unitPrice, quantityInTier, tierPrice };
  });
}

/** Volume (classic): entire quantity at the matching bracket's unitPrice. */
export function calculateVolumePrice(quantity, pricingTiers) {
  const qty = parseFloat(quantity) || 0;
  const sorted = [...(pricingTiers || [])].sort(
    (a, b) => (parseFloat(a?.from) || 0) - (parseFloat(b?.from) || 0)
  );
  // Brackets are whole-number ranges, so a fractional quantity (100.5) falls between two of them:
  // it belongs to the bracket whose upper bound it does not exceed. Mirrors the server engine.
  for (let i = 0; i < sorted.length; i += 1) {
    const tier = sorted[i];
    const from = parseFloat(tier?.from) || 0;
    const to =
      tier?.to !== undefined && tier?.to !== "" ? parseFloat(tier.to) : Infinity;
    const unitPrice = parseFloat(tier?.unitPrice) || 0;
    if (qty <= to && (i > 0 || qty > from - 1)) {
      return round2(qty * unitPrice);
    }
  }
  return 0;
}

function calculateTieredPrice(quantity, tiers) {
  return computeTierBreakdown(tiers, quantity).reduce((sum, row) => sum + row.tierPrice, 0);
}

/**
 * Base price for a plan/product under a given pricing model, converted to
 * the selected billing cycle if it differs from its own cycle. `amount` is
 * the flat/per-unit base price (a real catalog entity has this at
 * `entity.amount` for a Plan, `entity.price` for a Product - there's no
 * `priceDetails` wrapper on the real API shape, unlike an earlier version
 * of this function assumed); `pricingTiers` is the entity's own top-level
 * `{from,to,unitPrice}` bracket array, shared by Volume and Tiered models.
 */
export function calculatePlanPrice({
  pricingModel,
  amount,
  pricingTiers,
  seats,
  currentPlanCycle,
  selectedBillingCycle,
}) {
  let totalPrice = 0;

  switch (pricingModel) {
    case "Per Unit":
      // Server repriceLineItem overwrites tampered totals on save (amount × qty).
      totalPrice = (parseFloat(amount) || 0) * (parseFloat(seats) || 0);
      break;

    case "Flat Fee":
      // Flat Fee ignores seats/quantity — money is the catalog amount only.
      // Server repriceFlatFeeItems overwrites any client tampering on save.
      totalPrice = parseFloat(amount) || 0;
      break;

    case "Volume":
      // Classic: all units at matching bracket rate (server utils/pricing)
      totalPrice = calculateVolumePrice(seats, pricingTiers);
      break;

    case "Tiered":
      // Graduated bands summed
      totalPrice = calculateTieredPrice(seats, pricingTiers);
      break;

    default:
      totalPrice = 0;
  }

  if (normalizeCycleLabel(currentPlanCycle) === normalizeCycleLabel(selectedBillingCycle)) {
    return round2(totalPrice);
  }

  const currentCycle = currentPlanCycle || selectedBillingCycle;
  const selectedCycle = selectedBillingCycle || currentPlanCycle;
  if (!currentCycle || !selectedCycle) {
    return round2(totalPrice);
  }

  return round2(convertAmountBetweenCycles(totalPrice, currentCycle, selectedCycle));
}

/**
 * Applies a flat-amount or percent discount to an amount, clamped at 0.
 * Ported from BillingItems/index.js's calculateDiscountedPrice.
 */
export function applyDiscount(amount, discount) {
  if (!discount?.value || isNaN(discount.value)) return amount;
  const value = parseFloat(discount.value);

  if (discount.type === "Percent") {
    return Math.max(amount - (amount * value) / 100, 0);
  }
  // "Flat" is the default type when unset, matching the old engine.
  return Math.max(amount - value, 0);
}

/**
 * A single line item's total: quantity * unit value, with its own
 * (optional) flat-or-percent discount applied. Ported from
 * LineItemsTable's calculateTotalPrice.
 */
export function calculateLineItemTotal({ quantity, value, discount, discountType }) {
  let price = (Number(quantity) || 1) * (Number(value) || 0);

  if (discountType === "Percent") {
    const percentDiscount = discount !== undefined ? (price * Number(discount)) / 100 : 0;
    price -= percentDiscount;
  } else if (discount !== undefined) {
    // "Flat" is the default type when unset, matching the old engine.
    price -= Number(discount);
  }

  return round2(price);
}

/**
 * A single tax row's amount: a flat value, or a percent of the pre-tax
 * amount it's applied against. Ported from TaxTable's calculateTotalTax.
 */
export function calculateTaxAmount(taxType, taxValue, amount) {
  const value = parseFloat(taxValue);
  const parsedAmount = parseFloat(amount);
  if (!taxValue || isNaN(value) || !amount || isNaN(parsedAmount)) return 0;

  if (taxType === "Percent") {
    return round2((parsedAmount * value) / 100);
  }
  // "Flat"
  return round2(value);
}

/**
 * The core roll-up: subtotal -> discount -> tax -> grand total. Ported from
 * BillingItems/index.js's memoized `calculatedAmounts` (lines 328-377) -
 * the one piece of that ~5,500-line tree that was already correctly
 * memoized (as a component-bound useMemo; here it's a pure function so
 * every caller memoizes its own call with useMemo instead of re-porting
 * the formula).
 *
 * `plans`/`products` items need a `totalPrice` field; `lineItems` items
 * need `totalPrice` + `discountApplied` (a line item's discount is either
 * folded into the pre-discount subtotal, or applied after - see
 * `discountApplied`); `taxes` items need `totalTax`. `discount` is
 * `{ type: "Flat" | "Percent", value }` or undefined/null (no discount).
 */
export function calculateEntityTotals({
  plans = [],
  products = [],
  lineItems = [],
  discount,
  taxes = [],
}) {
  const totalPlansAmount = plans.reduce((acc, p) => acc + (parseFloat(p?.totalPrice) || 0), 0);
  const totalProductsAmount = products.reduce((acc, p) => acc + (parseFloat(p?.totalPrice) || 0), 0);

  const beforeDiscountTotal = lineItems
    .filter((item) => item?.discountApplied)
    .reduce((acc, item) => acc + (parseFloat(item?.totalPrice) || 0), 0);
  const afterDiscountTotal = lineItems
    .filter((item) => !item?.discountApplied)
    .reduce((acc, item) => acc + (parseFloat(item?.totalPrice) || 0), 0);

  const subtotal = totalPlansAmount + totalProductsAmount + beforeDiscountTotal;
  const discountedAmount = applyDiscount(subtotal, discount);
  const totalAmountBeforeTax = discountedAmount + afterDiscountTotal;

  const totalTax = taxes.reduce((acc, t) => acc + (parseFloat(t?.totalTax) || 0), 0);
  const totalAmount = totalAmountBeforeTax + totalTax;

  return {
    subtotal: round2(subtotal),
    discountedAmount: round2(discountedAmount),
    totalAmountBeforeTax: round2(totalAmountBeforeTax),
    totalTax: round2(totalTax),
    totalAmount: round2(totalAmount),
  };
}

// ─── Validators ─────────────────────────────────────────────────────────
// Pure - return a result, no showToast side effect. The old engine had 2-4
// near-identical copies of each of these, all calling showToast directly
// from inside the validator; the caller (a form field's onChange/onBlur)
// now decides how/whether to surface `.message`.

export function validateNumberInput(value, { fieldName = "Value", min = 0, max = null } = {}) {
  if (value === "" || value === null || value === undefined) {
    return { isValid: true, value: 0 };
  }
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return { isValid: false, value: 0, message: `Please enter a valid number for ${fieldName}` };
  }
  if (numValue < min) {
    return { isValid: false, value: min, message: `${fieldName} cannot be less than ${min}` };
  }
  if (max !== null && numValue > max) {
    return { isValid: false, value: max, message: `${fieldName} cannot be greater than ${max}` };
  }
  return { isValid: true, value: numValue };
}

export function validateQuantity(value) {
  if (value === "" || value === null || value === undefined) {
    return { isValid: true, value: "" };
  }
  if (!/^\d+$/.test(value)) {
    return { isValid: false, value: "", message: "Quantity must be a whole number" };
  }
  const numValue = parseInt(value, 10);
  if (numValue < 0) {
    return { isValid: false, value: "0", message: "Quantity cannot be negative" };
  }
  return { isValid: true, value };
}

export function validateDiscount(value, discountType, maxValue = null) {
  if (value === "" || value === null || value === undefined) {
    return { isValid: true, value: 0 };
  }
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return { isValid: false, value: 0, message: "Please enter a valid number for discount" };
  }
  if (numValue < 0) {
    return { isValid: false, value: 0, message: "Discount cannot be negative" };
  }
  if (discountType === "Percent" && numValue > 100) {
    return { isValid: false, value: 100, message: "Percentage discount cannot exceed 100%" };
  }
  if (discountType === "Flat" && maxValue !== null && numValue > maxValue) {
    return {
      isValid: false,
      value: maxValue,
      message: `Discount cannot exceed the total price (${maxValue.toFixed(2)})`,
    };
  }
  return { isValid: true, value: numValue };
}

export function validateTax(value, taxType, maxValue = null) {
  if (value === "" || value === null || value === undefined) {
    return { isValid: true, value: 0 };
  }
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return { isValid: false, value: 0, message: "Please enter a valid number for tax" };
  }
  if (numValue < 0) {
    return { isValid: false, value: 0, message: "Tax cannot be negative" };
  }
  if (taxType === "Percent" && numValue > 100) {
    return { isValid: false, value: 100, message: "Percentage tax cannot exceed 100%" };
  }
  if (maxValue !== null && numValue > maxValue) {
    return { isValid: false, value: maxValue, message: `Tax cannot exceed ${maxValue.toFixed(2)}` };
  }
  return { isValid: true, value: numValue };
}

/**
 * Full-precision currency formatting, e.g. "$1,234.56". Distinct from
 * _utils/formatNumber.js's formatCurrencyAbbreviated, which abbreviates
 * (1.2k/3.4M/...) - the right choice for a dashboard metric, the wrong one
 * for a line-item ledger where the exact billed amount has to be visible.
 */
export function formatCurrency(amount, sign = "") {
  const number = Number(amount);
  if (amount === null || amount === undefined || isNaN(number)) {
    return `${sign}0.00`;
  }
  return `${sign}${number.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
